import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import { proprietaMisurataIcons } from "@/utils/proprietaMisurataIcons";
import {
  isDanger,
  isOld,
  proprietaMisurataFormat,
  proprietaMisurataGetPercent,
} from "@/utils/proprietaMisurataUtils";
import { Card, Icon, SvgIcon } from "@mui/material";
import { ComponentProps } from "react";
import typography from "@/theme/base/typography";
import moment from "moment";
import configs from "./configs";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  ArcElement,
} from "chart.js";
import Button from "@/elements/Button";
import {
  NodoDto,
  ProprietaMisurataDto,
  ProprietaMisurataValueDto,
} from "@/api";
import NovusCard from "./NovusCard";

ChartJS.register(
  LineController,
  LineElement,
  ArcElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale
);

const { size } = typography;

function ProprietaMisurataCard({
  dispositivo,
  proprietaMisurata,
  proprietaMisurataFft,
  value,
  openHistoryDialog,
}: {
  dispositivo: NodoDto;
  proprietaMisurata: ProprietaMisurataDto;
  proprietaMisurataFft?: ProprietaMisurataDto[];
  value: ProprietaMisurataValueDto | null;
  openHistoryDialog?: () => void;
}) {
  const type = proprietaMisurata.type;
  const Icon_ = proprietaMisurataIcons[type.icona];

  const oldValue = isOld(value);

  if (!type || !Icon_) {
    return null;
  } else if (
    proprietaMisurata.type.influxDbTag === "rms_trend" ||
    proprietaMisurata.type.influxDbTag === "trendTemp" ||
    proprietaMisurata.type.influxDbTag === "novaDiagnostic"
  ) {
    return (
      <NovusCard
        dispositivo={dispositivo}
        proprietaMisurata={proprietaMisurata}
        proprietaMisurateFft={proprietaMisurataFft}
        value={value}
        oldValue={oldValue}
      />
    );
  }

  const valueText =
    typeof value?.value === "number"
      ? proprietaMisurataFormat(type, value.value)
      : "-";
  const label = type.label;
  const perc =
    typeof value?.value === "number"
      ? proprietaMisurataGetPercent(type, value.value)
      : undefined;

  const danger = isDanger(value);

  const color: ComponentProps<typeof Typography>["color"] = danger
    ? "error"
    : "success";

  const { data, options } = configs([], {
    label: "a",
    data: perc ? [perc, 100 - perc] : [0, 100],
    backgroundColors: [danger ? "error" : "success", "light"],
  });

  return (
    <Card sx={{ minWidth: 320, maxWidth: 320, minHeight: 320, maxHeight: 320 }}>
      <Box
        p={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flex={1}
      >
        <Typography variant="body2" fontWeight="medium" color="text">
          {label}
        </Typography>

        <Box height={200}>
          <Doughnut data={data} options={options} />
        </Box>

        <Box display="flex" alignItems="center" mb={0.5}>
          <Box
            width="1.25rem"
            height="1.25rem"
            color="grey-400"
            fontSize={size.lg}
            display="flex"
            justifyContent="center"
            alignItems="center"
            mr={1}
            variant="contained"
            ml={danger ? 4 : 0}
          >
            <SvgIcon>{Icon_()}</SvgIcon>
          </Box>

          <Typography
            variant="h4"
            fontWeight="bold"
            color={typeof value?.value === "number" ? color : "warning"}
          >
            {valueText}
          </Typography>

          {danger && (
            <Typography variant="h4" color={color}>
              <Icon sx={{ mt: 1, ml: 2 }}>warning</Icon>
            </Typography>
          )}
        </Box>

        {oldValue && (
          <Box>
            <Typography variant="caption" fontWeight="bold" color="warning">
              {value
                ? `Ultimo rilevamento: ${moment(value.timestamp).format(
                    "DD/MM/YYYY HH:mm:ss"
                  )}`
                : "Dato non rilevato"}
            </Typography>
          </Box>
        )}

        <Box width="100%" display="flex" flexDirection="row" mt="auto">
          <Button
            variant="textArrow"
            color="text"
            sx={{ ml: "auto", mr: 0, mt: 1, mb: -1 }}
            onClick={openHistoryDialog}
          >
            Vedi storico
          </Button>
        </Box>
      </Box>
    </Card>
  );
}

export default ProprietaMisurataCard;
